import { useEffect, useState, type ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";

interface ProtectedRouteProps {
  children: ReactNode;
}

const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const [checking, setChecking] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [approved, setApproved] = useState(false);
  const location = useLocation();

  useEffect(() => {
    let active = true;

    const evaluate = async (session: { user: { id: string } } | null) => {
      if (!session) {
        if (!active) return;
        setAuthed(false);
        setApproved(false);
        setChecking(false);
        return;
      }
      const { data } = await supabase
        .from("profiles")
        .select("status")
        .eq("id", session.user.id)
        .maybeSingle();
      if (!active) return;
      setAuthed(true);
      setApproved(data?.status === "approved");
      setChecking(false);
    };

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      evaluate(session);
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      evaluate(session);
    });

    return () => {
      active = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  if (checking) {
    return <div className="min-h-screen bg-cream-50" aria-hidden />;
  }

  if (!authed) {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (!approved) {
    return <Navigate to="/login" state={{ from: location.pathname, pending: true }} replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
